import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, List, ListItem, ListItemText, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Avatar, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import FixedSnackbar from './FixedSnackbar';

export default function BranchManagement() {
  const { t } = useTranslation();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.BRANCHES, { credentials: 'include' });
      if (!res.ok) {
        setBranches([]);
        setLoading(false);
        return;
      }
      const data = await res.json();
      const withCounts = await Promise.all((data || []).map(async (b) => {
        try {
          const id = b.branch_id || b.code || b._id;
          const r = await fetch(API_ENDPOINTS.BRANCH_USER_COUNT(id), { credentials: 'include' });
          if (r.ok) {
            const cd = await r.json();
            return { ...b, user_count: cd.user_count || 0 };
          }
        } catch (e) {
          // ignore
        }
        return { ...b, user_count: 0 };
      }));
      setBranches(withCounts);
    } catch (e) {
      setBranches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [form, setForm] = useState({ branch_id: '', name: '', address: '', country: '', status: 'active', config: { color: '' } });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const openCreate = () => {
    setEditingBranch(null);
    setForm({ branch_id: '', name: '', address: '', country: '', status: 'active', config: { color: '' } });
    setOpenDialog(true);
  };

  const openEdit = async (b) => {
    const id = b.branch_id || b.code || b._id;
    try {
      const res = await fetch(`${API_ENDPOINTS.BRANCHES}/${id}`, { credentials: 'include' });
      let full = b;
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        full = data.branch || data || b;
      }
      const addr = full.address || (full.config && full.config.location && full.config.location.address) || '';
      const color = (full.config && full.config.color) || full.color || '';
      setEditingBranch(id);
      setForm({ branch_id: id, name: full.name || '', address: addr, country: full.country || '', status: full.status || 'active', config: { color } });
      setOpenDialog(true);
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('branch_error_loading'), severity: 'error' });
    }
  };

  const handleSave = async () => {
    try {
      if (editingBranch) {
        const res = await fetch(`${API_ENDPOINTS.BRANCHES}/${editingBranch}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form)
        });
        if (!res.ok) throw new Error('update_failed');
        setSnackbar({ open: true, message: t('branch_updated'), severity: 'success' });
      } else {
        const res = await fetch(API_ENDPOINTS.BRANCHES, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const msg = err.message || err.message_key || 'create_failed';
          throw new Error(msg);
        }
        setSnackbar({ open: true, message: t('branch_created'), severity: 'success' });
      }
      setOpenDialog(false);
      fetchBranches();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('branch_save_error'), severity: 'error' });
    }
  };

  const handleDelete = async (branchId) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.BRANCHES}/${branchId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = err.message || err.message_key || 'delete_failed';
        throw new Error(msg);
      }
      setConfirmDelete(null);
      setSnackbar({ open: true, message: t('branch_deleted'), severity: 'success' });
      fetchBranches();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('branch_delete_error'), severity: 'error' });
    }
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Chip label={`${branches.length} ${t('branch_management')}`} size="small" sx={{ height: 24 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchBranches}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {t('refresh')}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }}
          >
            {t('create')}
          </Button>
        </Box>
      </Box>

      {/* Branch List */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : branches.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Avatar sx={{ width: 48, height: 48, mb: 1, bgcolor: 'grey.100', color: 'grey.400', mx: 'auto' }}>
            <AccountTreeIcon sx={{ fontSize: 24 }} />
          </Avatar>
          <Typography variant="body2" color="text.secondary">
            {t('no_branches_found')}
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {branches.map((b, idx) => (
            <ListItem
              key={b.branch_id || b.code || b._id}
              sx={{
                px: 1.5,
                py: 1.5,
                borderRadius: 1.5,
                mb: 1,
                border: 1,
                borderColor: 'divider',
                '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
                transition: 'all 0.15s'
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" onClick={() => openEdit(b)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setConfirmDelete(b.branch_id || b.code || b._id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  mr: 1.5,
                  bgcolor: (b.config && b.config.color) || b.color || 'grey.300',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  border: '1px solid rgba(0,0,0,0.1)'
                }}
              >
                {(b.name || '?').charAt(0).toUpperCase()}
              </Avatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {b.name}
                    </Typography>
                    <Chip label={`${b.user_count || 0}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    {b.branch_id || b.code || ''} — {b.country || ''}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingBranch ? t('edit_branch') : t('create_branch')}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editingBranch && (
              <TextField label={t('branch_id')} value={form.branch_id} fullWidth size="small" disabled />
            )}
            <TextField label={t('name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth size="small" />
            <TextField label={t('address')} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} fullWidth size="small" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('branch_color')}
              </Typography>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  bgcolor: (form.config && form.config.color) || '#000000',
                  border: '2px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': { borderColor: 'primary.main' }
                }}
                onClick={() => document.getElementById('branch-color-input').click()}
              >
                <input
                  id="branch-color-input"
                  type="color"
                  value={(form.config && form.config.color) || '#000000'}
                  onChange={(e) => setForm({ ...form, config: { ...(form.config || {}), color: e.target.value } })}
                  style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {(form.config && form.config.color) || '#000000'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('confirm_delete_branch')}
          </Typography>
          <IconButton onClick={() => setConfirmDelete(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('confirm_delete_branch_prompt')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(confirmDelete)} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
        autoHideDuration={6000}
      />
    </Box>
  );
}
